import React, { createContext, useContext, useEffect, useState, useMemo, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import { AppShell, IAppShellConfig } from './components/AppShell/AppShell';
import { DeleteConfirmationModal, IDeleteConfirmationModalConfig, IDeleteConfirmationModalProps } from './components/modals/DeleteConfirmationModal';
import { ApiContext } from './contexts/ApiProvider';
import { AuthContext } from './contexts/AuthProvider';
import { IApiMeResponse } from './models/api/me/IApiMeResponse';
import { IApiProjectSummaryResponse } from './models/api/project/IApiProjectSummaryResponse';
import { Home } from './pages/home/Home';
import { Profile } from './pages/profile/Profile';
import { ProjectContacts } from './pages/project-contacts/ProjectContacts';
import { ProjectCoreData } from './pages/project-coredata/ProjectCoreData';
import { ProjectDocuments } from './pages/project-documents/ProjectDocuments';
import { ProjectHome } from './pages/project-home/ProjectHome';
import { ProjectObservers } from './pages/project-observers/ProjectObservers';
import { ProjectParties } from './pages/project-parties/ProjectParties';
import { ProjectProtocols } from './pages/project-protocols/ProjectProtocols';
import { ProjectSafetyChecklist } from './pages/project-safetychecklist/ProjectSafetyChecklist';
import { Projects } from './pages/projects/Projects';
import { Settings } from './pages/settings/Settings';
import { Users } from './pages/users/Users';
import { Contacts } from './pages/templates/Contacts';
import { Deficits } from './pages/templates/Deficits';
import { Trades } from './pages/templates/Trades';
import { Checklists } from './pages/templates/Checklists';
import { IApiOrganizationResponse } from './models/api/organization/IApiOrganizationResponse';
import { OrganizationContext } from './contexts/OrganizationProvider';
import algoliasearch from 'algoliasearch';
import 'instantsearch.css/themes/algolia.css';
import { SearchClient } from 'algoliasearch';
import '@algolia/autocomplete-theme-classic';
import { IApiRoleResponse } from './models/api/roles/IApiRoleResponse';
import { Password } from './pages/password/Password';


interface IAppContext {
  me: IApiMeResponse;
  projects: IApiProjectSummaryResponse[];
  projectsAge: Date;
  roles: { values: IApiRoleResponse[]; dictionary: { [key: string]: { name: string; description: string } } };
  addProject(project: IApiProjectSummaryResponse): void;
  updateProject(project: IApiProjectSummaryResponse): void;
  setAppShellConfig: React.Dispatch<React.SetStateAction<IAppShellConfig | undefined>>;
  setDCM: React.Dispatch<React.SetStateAction<IDeleteConfirmationModalConfig | undefined>>;
  setMe: React.Dispatch<React.SetStateAction<IApiMeResponse | undefined>>;
  reloadProjects(): void;
  organizations: IApiOrganizationResponse[];
  searchClient: SearchClient;
  searchApiKey: string;
}

export const AppContext = createContext({} as IAppContext);

function App() {

  const { getMe, getAllProjects, getOrganization, getSearchApiKey, getAllRoles } = useContext(ApiContext);
  const { available, selected } = useContext(OrganizationContext);

  const [me, setMe] = useState<IApiMeResponse | undefined>(undefined);
  const [projects, setProjects] = useState<{ retrievedAt: Date, data: IApiProjectSummaryResponse[] }>({ retrievedAt: (new Date()), data: [] });
  const [roles, setRoles] = useState<{ values: IApiRoleResponse[]; dictionary: { [key: string]: { name: string; description: string } } }>({ values: [], dictionary: {} });
  const [appShellConfig, setAppShellConfig] = useState<IAppShellConfig | undefined>(undefined);
  const [dcm, setDCM] = useState<IDeleteConfirmationModalConfig | undefined>(undefined);
  const [organizations, setOrganizations] = useState<IApiOrganizationResponse[]>([]);
  const [searchApiKey, setSearchApiKey] = useState<string>("");

  useEffect(() => {
    if(!me) {
      getMe().then(me => {
        //setMe(me);
        getAllProjects().then(projects => {
          getAllRoles().then(roles => {
            var lookup: { [key: string]: { name: string; description: string } } = {};
            roles.forEach(r => lookup[r.id] = { name: r.name, description: r.description })
            setRoles({
              values: roles,
              dictionary: lookup
            });
            setProjects({ retrievedAt: (new Date()), data: projects });
            setMe(me); // -> init done
          });
        });
      });
    }
  }, [me, getMe, getAllProjects]);

  useEffect(() => {
    var tasks = available.map(a => getOrganization(a.id));
    Promise.all(tasks).then(values => setOrganizations(values));
  }, [available]);

  useEffect(() => {
    if(!selected) {
      return;
    }

    const updateSearchApiKey = () => {
      getSearchApiKey().then(result => {
        setSearchApiKey(result.apiKey);
      });
    };
    updateSearchApiKey();

    var timer = setInterval(() => updateSearchApiKey(), 1000 * 60 * 55); // refresh every 55 minutes
    return () => clearInterval(timer);
  }, [selected]);

  const searchClient = useMemo(() => {
    if(searchApiKey) {
      return algoliasearch(
        'SD35WODKBG',
        searchApiKey
      );
    } else {
      return undefined;
    }
  }, [searchApiKey]);

  const addProject = (project: IApiProjectSummaryResponse) => {
    setProjects(oldState => ({ ...oldState, data: [...(oldState.data), project] }));
  };

  const updateProject = (project: IApiProjectSummaryResponse) => {
    setProjects(oldState => {
      const index = oldState.data.findIndex(p => p.id === project.id);
      if(index < 0) {
        return { ...oldState, data: [...oldState.data, project] };
      } else {
        var newState = { ...oldState, data: [...oldState.data] };
        newState.data.splice(index, 1, {
          ...oldState.data[index],
          ...project
        });
        return newState;
      }
    });
  };

  const reloadProjects = useMemo(() => () => {
    var promise = getAllProjects();
    toast.promise(promise, {
      loading: `Projekte werden aktualisiert...`,
      success: (result) => {
        setProjects({ retrievedAt: (new Date()), data: result });
        return `Projekte wurden erfolgreich aktualisiert!`;
      },
      error: (code: number) => {
        var message = `Fehler beim Aktualisieren der Projekte!`;
        switch(code) {
          case 400: {
            message += `Fehler 400`;
            break;
          }
          case 404: {
            message += `Fehler 404`;
            break;
          }
          case 500: {
            message += `Fehler 500`;
            break;
          }
        }
        return message;
      },
    });
    return promise;
  }, [getAllProjects, setProjects]);

  if(typeof(searchClient) === 'undefined') {
    return (<h1>Loading app settings...</h1>); 
  }

  if(!me) {
    return (<h1>Loading app settings...</h1>); 
  }

  return (
    <AppContext.Provider
      value={{
        me,
        projects: projects.data,
        projectsAge: projects.retrievedAt,
        roles,
        addProject,
        updateProject,
        setAppShellConfig,
        setDCM,
        setMe,
        reloadProjects,
        organizations,
        searchClient,
        searchApiKey
      }}
    >
      <BrowserRouter basename='/web'>
        <Toaster 
          position="bottom-right"
          gutter={8}
          toastOptions={{
            style: {
              fontSize: 'var(--chakra-fontSizes-sm)',
            },
          }}
        />
        <AppShell config={appShellConfig}>
          <Routes>
            <Route path="/"
                   element={
                    <Home />
                   }
            />
            <Route path="/login"
                   element={
                    <Navigate replace to="/" />
                   }
            />
            <Route path="/profile"
                   element={
                    <Profile />
                   }
            />
            <Route path="/profile/password"
                   element={
                    <Password />
                   }
            />
            <Route path="/projects"
                   element={
                    <Projects />
                   }
            />
            <Route path="/templates/contacts"
                   element={
                    <Contacts />
                   }
            />
            <Route path="/templates/deficits"
                   element={
                    <Deficits />
                   }
            />
            <Route path="/templates/trades"
                   element={
                    <Trades />
                   }
            />
            <Route path="/templates/checklists"
                   element={
                    <Checklists />
                   }
            />
            <Route path="/settings"
                   element={
                    <Settings />
                   }
            />
            <Route path="/users"
                   element={
                    <Users />
                   }
            />
            <Route path="/projects/:projectId"
                   element={
                    <ProjectHome />
                   }
            />
            <Route path="/projects/:projectId/protocols"
                   element={
                    <ProjectProtocols />
                   }
            />
            <Route path="/projects/:projectId/contacts"
                   element={
                    <ProjectContacts />
                   }
            />
            <Route path="/projects/:projectId/coredata"
                   element={
                    <ProjectCoreData />
                   }
            />
            <Route path="/projects/:projectId/parties"
                   element={
                    <ProjectParties />
                   }
            />
            <Route path="/projects/:projectId/observers"
                   element={
                    <ProjectObservers />
                   }
            />
            <Route path="/projects/:projectId/documents"
                   element={
                    <ProjectDocuments />
                   }
            />
            <Route path="/projects/:projectId/safetychecklist"
                   element={
                    <ProjectSafetyChecklist />
                   }
            />
          </Routes>
        </AppShell>
        <DeleteConfirmationModal clear={() => setDCM(undefined)} {...dcm} />
      </BrowserRouter>
    </AppContext.Provider>
  );
}

export default App;